import React, { useEffect, useState } from "react";
import Icon from "@mui/material/Icon";
import { TablePagination, Grid, Card, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { getTrunkRequest, deleteTrunkRequest, changeTrunkStatusRequest } from "../../../api/api";
import Loader from "../../../Loader/loader";
import DeleteForm from "../../DeleteAlert/DeleteForm";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AddTrunkForm from "../../form/AddTrunk";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import MDInput from "components/MDInput";

function TrunkTable() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    try {
      await changeTrunkStatusRequest(id, newStatus);
      fetchData();
    } catch (error) {
      console.error("Failed to toggle status:", error);
    }
  };

  const handleConfirmedDelete = async () => {
    setIsSubmittingDelete(true);
    try {
      await deleteTrunkRequest(deleteId);
      fetchData();
      setIsDeleteOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting trunk:", error);
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  const columns = [
    { Header: "trunk name", accessor: "trunk_name", align: "left" },
    { Header: "ip", accessor: "ip_address", align: "left" },
    { Header: "prefix", accessor: "prefix", align: "center" },
    { Header: "failover_name", accessor: "failover_name", align: "center" },
    { Header: "priority", accessor: "priority", align: "center" },
    { Header: "port", accessor: "port", align: "center" },
    { Header: "status", accessor: "status", align: "center" },
    { Header: "action", accessor: "action", align: "center" },
  ];

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getTrunkRequest(page + 1, perPage, searchTerm);
      const trunkData = res?.data?.data?.data || [];
      const total = res?.data?.data?.total || 0;

      const formattedRows = trunkData.map((item) => ({
        trunk_name: (
          <MDTypography variant="button" fontWeight="medium">
            {item.name}
          </MDTypography>
        ),
        ip_address: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.ip}
          </MDTypography>
        ),
        prefix: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.prefix || "N.A"}
          </MDTypography>
        ),
        failover_name: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.failover_name || "N.A"}
          </MDTypography>
        ),
        priority: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.priority || "N.A"}
          </MDTypography>
        ),
        port: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.port || "N.A"}
          </MDTypography>
        ),
        status: (
          <MDTypography
            variant="caption"
            color={item.status === 1 ? "success" : "error"}
            fontWeight="medium"
          >
            {item.status === 1 ? "active" : "inactive"}
          </MDTypography>
        ),
        action: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon
              color="info"
              fontSize="small"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setEditData(item);
                setOpen(true);
              }}
            >
              <EditIcon />
            </Icon>
            <Icon
              color="error"
              fontSize="small"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setDeleteId(item.id);
                setIsDeleteOpen(true);
              }}
            >
              <DeleteIcon />
            </Icon>
            <Icon
              fontSize="medium"
              sx={{ cursor: "pointer" }}
              color={item.status === 1 ? "success" : "action"}
              onClick={() => handleStatusToggle(item.id, item.status)}
            >
              {item.status === 1 ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </Icon>
          </MDBox>
        ),
      }));

      setRows(formattedRows);
      setTotalCount(total);
    } catch (error) {
      console.error("Error fetching trunks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    fetchData();
  }, [page, perPage]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                Trunk Table
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDInput
                  placeholder="Search here"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: "10%",
                    minWidth: "200px",
                  }}
                />
                <MDButton
                  onClick={() => setOpen(true)}
                  startIcon={<AddBoxIcon />}
                  sx={{
                    backgroundColor: "#ffffff",
                    color: "#1A73E8",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                      color: "#1A73E8",
                    },
                  }}
                >
                  Add Trunk
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox pt={3}>
              {isLoading ? (
                <Grid container justifyContent="center" sx={{ py: 5 }}>
                  <Loader />
                </Grid>
              ) : rows.length === 0 ? (
                <MDTypography sx={{ p: 3, textAlign: "center", color: "gray" }}>
                  No Record Found
                </MDTypography>
              ) : (
                <>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    loading={false}
                  />
                  <TablePagination
                    count={totalCount}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(event) => {
                      const newPerPage = parseInt(event.target.value, 10);
                      setPerPage(newPerPage);
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                </>
              )}
            </MDBox>
          </Card>
        </Grid>
      </MDBox>

      {/* Add/Edit Modal */}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditData(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editData ? "Edit Trunk" : "Add Trunk"}</DialogTitle>
        <DialogContent>
          <AddTrunkForm
            onSuccess={() => {
              setOpen(false);
              setEditData(null);
              fetchData();
            }}
            initialData={editData}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DeleteForm
        isOpen={isDeleteOpen}
        handleDelete={handleConfirmedDelete}
        onHandleClose={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        isSubmitting={isSubmittingDelete}
      />
    </DashboardLayout>
  );
}

export default TrunkTable;
